/**
 * The one place that PUTs bytes to a presigned Vault upload target, plus the
 * bounded queue both upload paths run their PUTs through.
 *
 * Extracted because the single-file and batch hooks each carried a byte-identical
 * copy of the signed-header contract below — two copies means the next fix to it
 * lands in one of them.
 *
 * Typed on the structural shape rather than a generated type: `createVaultDocument`
 * and `createVaultDocumentsBatch` return different named types for what is the same
 * `{ url, method, headers }` target.
 */

/** How many byte PUTs are in flight at once — file parts, or whole files. */
export const PUT_CONCURRENCY = 4;

/** The subset of a presigned upload target these helpers need. */
export type PresignedTarget = {
  url: string;
  method: string;
  headers: { name: string; value: string }[];
};

/**
 * PUT the raw bytes to a presigned upload target, applying its headers verbatim.
 *
 * NOTE: `fetch` with a `File` body auto-adds `Content-Type: file.type` unless we
 * set it explicitly — so the backend MUST include the signed `Content-Type` in
 * `target.headers` (which we apply here, overriding the browser default). If the
 * presigned URL is signed with a Content-Type the browser's guess doesn't match
 * and the header isn't in `target.headers`, S3 can reject the PUT with 403.
 *
 * `label` names the file in the error, so a failure inside a multi-file batch says
 * which one; omit it for a single-file upload where there's no ambiguity.
 */
export const putBytesToTarget = async (
  target: PresignedTarget,
  body: Blob,
  label?: string,
): Promise<void> => {
  const headers: Record<string, string> = {};
  target.headers.forEach((h) => {
    headers[h.name] = h.value;
  });
  const response = await fetch(target.url, { method: target.method, headers, body });
  if (!response.ok) {
    const what = label ? ` for "${label}"` : "";
    throw new Error(`Vault upload failed${what} (${response.status} ${response.statusText})`);
  }
};

/**
 * Run `task` over every item with at most {@link PUT_CONCURRENCY} in flight,
 * returning each item's outcome **positionally** — never throwing.
 *
 * Two reasons it settles rather than rejects: a multi-file batch has to be able to
 * finalize the files that landed even when one failed, and a multipart upload wants
 * every part's verdict rather than whichever rejected first. Callers that do want
 * all-or-nothing check the results themselves.
 *
 * Serial PUTs were the alternative, and they undo the point of batching: a 20-file
 * drop became 20 sequential round trips, likely slower end to end than the
 * per-file path it replaced.
 */
export const runWithConcurrency = async <T>(
  items: readonly T[],
  task: (item: T, index: number) => Promise<void>,
  onSettled?: (completed: number, total: number) => void,
): Promise<(Error | null)[]> => {
  const outcomes: (Error | null)[] = new Array<Error | null>(items.length).fill(null);
  const queue = items.map((_, index) => index);
  let completed = 0;

  const worker = async (): Promise<void> => {
    for (;;) {
      const index = queue.shift();
      if (index === undefined) return;
      try {
        await task(items[index], index);
      } catch (e) {
        outcomes[index] = e instanceof Error ? e : new Error(String(e));
      }
      completed += 1;
      onSettled?.(completed, items.length);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(PUT_CONCURRENCY, items.length) }, () => worker()),
  );
  return outcomes;
};
